import { useQuery, useQueryClient } from "@tanstack/react-query";

/**
 * @callback InvalidateKeyFnc
 * @param {string|string[]} key - Query key to invalidate
 * @returns {Promise<void>}
 */

/**
 * @param {Object} params
 * @param {import("@tanstack/react-query").QueryClient} params.queryClient
 * @returns {InvalidateKeyFnc}
 */
export function invalidateKey({ queryClient }) {
	return /** @type {InvalidateKeyFnc} */ (
		(key) => {
			queryClient.invalidateQueries({
				queryKey: /** @type {readonly unknown[]} */ ([key]).flat(),
			});
		}
	);
}

/**
 * @typedef {Object} UseFetchReturn
 * @property {*} data
 * @property {Error} [error]
 * @property {boolean} isLoading
 * @property {boolean} isFetching
 * @property {boolean} isSuccess
 * @property {boolean} isError
 * @property {InvalidateKeyFnc} invalidateKey
 * @property {function} refetch
 */

/**
 * @template T
 * @param {import("./queries/queries").Query<T>} query
 * @param {{ args?: T, keys?: "auto" | string[], enabled?: boolean }} [options]
 * @returns {UseFetchReturn}
 *
 * @example
 * const { data, isLoading, invalidateKey } = useFetchFromQuery(itemQueries.getAll, {
 *   args: { crewId },
 *   enabled: Boolean(rewId),
 * });
 */
export function useFetchFromQuery(query, { args = {}, keys = "auto", enabled = false } = {}) {
	if (keys === "auto") {
		keys = Object.values(args).map((_key) => _key?.toString());
	}

	return useFetch({
		queryKey: [...query.key, ...keys],
		queryFnc: () => query.run(args),
		enabled,
	});
}

export function useFetch({ queryKey, queryFnc, enabled = true }) {
	const queryClient = useQueryClient();

	const useQueryResult = useQuery({
		queryKey,
		queryFn: queryFnc,
		enabled,
	});

	/** @type {UseFetchReturn} */
	const result = /** @type {any} */ (useQueryResult);
	result.invalidateKey = invalidateKey({ queryClient });

	return result;
}
